/**
 * Task Details Panel Component
 *
 * Displays task information in the bottom panel.
 */

import type { ReactNode } from "react";
import type { Task } from "../lib/usePlanReasoning";
import Avatar from "./Avatar";

interface TaskDetailsProps {
  task: Task;
  hideHeader?: boolean;
}

// Check if a string is a valid URL
function isUrl(str: string): boolean {
  try {
    const url = new URL(str);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

// Render a value, converting URLs to clickable links
function renderValue(value: string): ReactNode {
  if (isUrl(value)) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="link link-info break-all"
      >
        {value}
      </a>
    );
  }
  return value;
}

export default function TaskDetails({ task, hideHeader }: TaskDetailsProps) {
  const statusColors: Record<string, string> = {
    backlog: "badge-ghost",
    ready: "badge-primary",
    in_progress: "badge-secondary",
    blocked: "badge-warning",
    done: "badge-success",
  };

  const statusLabels: Record<string, string> = {
    backlog: "Backlog",
    ready: "Ready",
    in_progress: "In Progress",
    blocked: "Blocked",
    done: "Done",
  };

  const agentTypeLabels: Record<string, string> = {
    C: "Coder",
    S: "Security",
    A: "Architect",
  };

  return (
    <div className="space-y-3 text-sm">
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-base">{task.id}</span>
          <span className={`badge badge-sm ${statusColors[task.status] || ""}`}>
            {statusLabels[task.status] || task.status}
          </span>
        </div>
      )}

      {/* Meta Description */}
      {task.meta?.description && (
        <p className="text-base-content/70">{task.meta.description}</p>
      )}

      {/* Info */}
      <div className="space-y-1.5">
        {task.agent && (task.status === "in_progress" || task.status === "done") && (
          <div className="flex items-center gap-2">
            <span className="text-base-content/50 w-16 text-xs">Agent</span>
            <Avatar name={task.agent} keyHash={task.agentKeyHash} size="sm" />
          </div>
        )}
        {task.agentType && !task.agent && (
          <div className="flex items-center gap-2">
            <span className="text-base-content/50 w-16 text-xs">Type</span>
            <span className="badge badge-outline badge-xs">
              {agentTypeLabels[task.agentType] || task.agentType}
            </span>
          </div>
        )}
        {task.meta?.priority && (
          <div className="flex items-center gap-2">
            <span className="text-base-content/50 w-16 text-xs">Priority</span>
            <span className={`badge badge-xs ${
              task.meta.priority === "high" ? "badge-error" :
              task.meta.priority === "medium" ? "badge-warning" : "badge-ghost"
            }`}>
              {task.meta.priority}
            </span>
          </div>
        )}
        {task.meta?.estimate && (
          <div className="flex items-center gap-2">
            <span className="text-base-content/50 w-16 text-xs">Estimate</span>
            <span className="text-xs">{task.meta.estimate}</span>
          </div>
        )}
        {task.blockedBy && task.blockedBy.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-base-content/50 w-20 text-xs">Depends on</span>
            <div className="flex flex-wrap gap-1">
              {task.blockedBy.map((dep) => (
                <span key={dep} className="badge badge-outline badge-xs font-mono">
                  {dep}
                </span>
              ))}
            </div>
          </div>
        )}
        {task.progress !== undefined && (
          <div className="flex items-center gap-2">
            <span className="text-base-content/50 w-16 text-xs">Progress</span>
            <progress
              className="progress progress-secondary w-20 h-1.5"
              value={task.progress}
              max="100"
            />
            <span className="text-xs text-base-content/50">{task.progress}%</span>
          </div>
        )}
        {task.meta?.tags && task.meta.tags.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-base-content/50 w-16 text-xs">Tags</span>
            <div className="flex flex-wrap gap-1">
              {task.meta.tags.map((tag) => (
                <span key={tag} className="badge badge-ghost badge-xs">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
        {task.meta?.source && (
          <div className="flex items-start gap-2">
            <span className="text-base-content/50 w-16 text-xs shrink-0">Source</span>
            <span className="font-mono text-xs">{renderValue(task.meta.source)}</span>
          </div>
        )}
        {task.meta?.createdAt && (
          <div className="flex items-center gap-2">
            <span className="text-base-content/50 w-16 text-xs">Created</span>
            <span className="text-xs">{task.meta.createdAt}</span>
          </div>
        )}
        {task.meta?.updatedAt && (
          <div className="flex items-center gap-2">
            <span className="text-base-content/50 w-16 text-xs">Updated</span>
            <span className="text-xs">{task.meta.updatedAt}</span>
          </div>
        )}
        {/* Show any additional meta fields */}
        {task.meta && Object.entries(task.meta)
          .filter(([key]) => !['description', 'priority', 'estimate', 'tags', 'source', 'createdAt', 'updatedAt', 'note'].includes(key))
          .filter(([, value]) => value !== undefined && value !== null)
          .map(([key, value]) => (
            <div key={key} className="flex items-start gap-2">
              <span className="text-base-content/50 w-16 text-xs capitalize shrink-0">{key.replace(/-/g, ' ')}</span>
              <span className="text-xs font-mono">
                {typeof value === 'string' ? renderValue(value) : String(value)}
              </span>
            </div>
          ))
        }
      </div>

      {/* Note */}
      {(task.note || task.meta?.note) && (
        <div className="p-2 bg-base-200 rounded text-xs">
          <span className="text-base-content/50">Note: </span>
          {task.note || task.meta?.note}
        </div>
      )}
    </div>
  );
}
